import { useRef, useState } from 'react'

// Every source whose failure marks the sync status line "Changes not saved" (page-errors.model.md I3).
const pageSaveSources = ['pageEdit', 'reader', 'readonly', 'translatable', 'remoteImport', 'comment', 'title', 'entry', 'entryDelete', 'cardFrozen', 'cardDelete', 'cardRequest', 'flush', 'childPage', 'createPage'] as const
const isSaveSource = (source: string) => (pageSaveSources as readonly string[]).includes(source)
export type PageErrorSource = typeof pageSaveSources[number] | 'signOut' | 'workspace' | 'reference' | `blockLink:${string}`

/** One owner per visible page error (page-errors.model.md): a writer reports only its own source, and its next success clears only that source. */
export function usePageErrors() {
  const [errors, setErrors] = useState<Partial<Record<PageErrorSource, unknown>>>({})
  // Sources with an error set or pending; a success with nothing to clear schedules no render (e.g. after unmount or outside act).
  const failing = useRef(new Set<PageErrorSource>())
  function report(source: PageErrorSource, error?: unknown) {
    if (error === undefined && !failing.current.has(source)) return
    if (error === undefined) failing.current.delete(source)
    else failing.current.add(source)
    setErrors(current => {
      if (error === undefined && !(source in current)) return current
      // Re-adding moves the source last, so the save alert shows the latest failure. No computed-key destructuring:
      // React Compiler skips a hook that uses it, and every page handler built on `report` changed identity per render.
      const rest = Object.fromEntries(Object.entries(current).filter(([key]) => key !== source))
      return error === undefined ? rest : { ...rest, [source]: error }
    })
  }
  /** Settles `task` into its source: success clears it, failure sets it; the result is the value or undefined. */
  function track<T>(source: PageErrorSource, task: Promise<T>) {
    return task.then(value => { report(source); return value }, (error: unknown): undefined => { report(source, error) })
  }
  const saveError = Object.entries(errors).findLast(([source]) => isSaveSource(source))?.[1]
  return { errors, report, track, saveError }
}
